/**
 * OnboardingWizard — modal first-run experience.
 *
 * 4 steps to value in under 2 minutes:
 *   1. Industry pick (sets firm_naics_code → unlocks vertical pack) + optional "Sign drafts
 *      as" (workspace-wide drafter_signoff_name — sent only when the user edits it)
 *   2. Connect HubSpot + QuickBooks (focused dual-connect)
 *   3. Sync progress — polls until first sync completes, then shows briefing
 *   4. Done — suggested first prompts + dashboard CTA
 *
 * Auto-shown to users with no firm_naics_code AND no integrations.
 * Dismissible (sets a localStorage flag) and re-openable via Settings.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { firstNameOf } from '@waypoint/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useBasePath } from '@/lib/config/nav-helpers';
import { getHubSpotAuthUrl } from '@/lib/hubspot';
import { NO_LIVE_MODEL_COPY } from '@/lib/ml-copy';
import { getQboAuthUrl } from '@/lib/quickbooks';
import { listIntegrations, type IntegrationRow } from '@/lib/integrations';
import { getMorningBriefing } from '@/lib/morning-briefing';
import { usePreference, readPreference } from '@/lib/hooks/use-preference';

interface SyncProviderStatus {
  provider: string;
  status: 'healthy' | 'degraded' | 'failing' | 'idle' | 'syncing';
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  itemsSynced24h: number;
  runsOk: number;
  runsError: number;
}

async function fetchSyncStatus(): Promise<{ providers: SyncProviderStatus[] }> {
  return api('/v1/sync-status');
}

interface IndustrySummary {
  naics_code: string;
  naics_label: string;
  sector: string;
}

const PREF_DISMISSED = 'onboarding_dismissed';
const PREF_SNOOZED = 'onboarding_snoozed_at';

const STEPS = ['Industry', 'Connect', 'Go Live'] as const;

/** Top-8 industries shown as visual cards. The rest are searchable via "Something else". */
const FEATURED_INDUSTRIES: { icon: string; keywords: string[] }[] = [
  { icon: '📊', keywords: ['accounting', 'cpa', 'tax', 'bookkeep'] },
  { icon: '⚖️', keywords: ['legal', 'law', 'attorney'] },
  { icon: '💼', keywords: ['consult', 'advisory', 'management consult'] },
  { icon: '🏠', keywords: ['real estate', 'property', 'realty'] },
  { icon: '🏥', keywords: ['health', 'medical', 'dental', 'clinic', 'physician'] },
  { icon: '🏗️', keywords: ['construct', 'building', 'contractor', 'architect'] },
  { icon: '💻', keywords: ['software', 'saas', 'technology', 'tech', 'it service'] },
  { icon: '🛍️', keywords: ['retail', 'ecommerce', 'e-commerce', 'shop', 'store'] },
];

interface Props {
  onComplete?: () => void;
  reopenRef?: React.MutableRefObject<(() => void) | null>;
}

/**
 * Slim "Continue setup" banner — shown when wizard was snoozed (not permanently dismissed).
 * One click reopens the wizard at the step they left off.
 */
export function ContinueSetupBanner({ onReopen }: { onReopen: () => void }) {
  const [, setSnoozedAt] = usePreference<number | null>(PREF_SNOOZED, null);
  const [visible, setVisible] = useState(() => {
    const s = readPreference<number | null>(PREF_SNOOZED, null);
    const d = readPreference<boolean>(PREF_DISMISSED, false);
    return !!s && !d;
  });

  if (!visible) return null;

  return (
    <div className="rounded-xl border border-primary/20 bg-primary/3 px-4 py-2.5 flex items-center justify-between gap-3 animate-fade-in">
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-primary-soft text-primary">
          <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 11l3 3L22 4M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></svg>
        </div>
        <span className="text-xs text-ink">Finish setting up Waypoint to unlock all features</span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => {
            setSnoozedAt(null);
            setVisible(false);
          }}
          className="text-2xs text-ink-dim hover:text-ink"
        >
          Dismiss
        </button>
        <button
          onClick={() => {
            setSnoozedAt(null);
            setVisible(false);
            onReopen();
          }}
          className="rounded-lg bg-primary text-white px-3 py-1.5 text-2xs font-semibold hover:opacity-90"
        >
          Continue setup →
        </button>
      </div>
    </div>
  );
}

export function OnboardingWizard({ onComplete, reopenRef }: Props) {
  const navigate = useNavigate();
  const basePath = useBasePath();
  const queryClient = useQueryClient();
  const [, setDismissed] = usePreference<boolean>(PREF_DISMISSED, false);
  const [, setSnoozed] = usePreference<number | null>(PREF_SNOOZED, null);

  const [step, setStep] = useState(0);
  const [pickedNaics, setPickedNaics] = useState<string | null>(null);
  const [pickedSector, setPickedSector] = useState<string | null>(null);
  const [savingIndustry, setSavingIndustry] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [connectingProvider, setConnectingProvider] = useState<string | null>(null);
  const [seedingSample, setSeedingSample] = useState(false);
  const [sampleChosen, setSampleChosen] = useState(false);
  const [syncElapsed, setSyncElapsed] = useState(0);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [industrySearch, setIndustrySearch] = useState('');
  const [showAllIndustries, setShowAllIndustries] = useState(false);
  const { user } = useAuth();
  const [signoffEdit, setSignoffEdit] = useState<string | null>(null);

  const settingsQ = useQuery({
    queryKey: ['onboarding-wizard-state'],
    queryFn: async () => {
      // NO catch-to-empty here: if these fetches fail (rate limit, blip,
      // expired session) we must NOT mistake the failure for "brand-new
      // user with nothing connected" — that re-opened the wizard for fully
      // onboarded users whenever the API hiccuped. Let the query error;
      // the open-effect only fires on confirmed data.
      const [settingsRes, integ] = await Promise.all([
        api<{ settings?: { firm_naics_code?: string | null; drafter_signoff_name?: string | null } }>('/v1/settings'),
        api<{ integrations: IntegrationRow[] }>('/v1/integrations'),
      ]);
      return {
        hasNaics: !!settingsRes.settings?.firm_naics_code,
        hasIntegration: (integ.integrations ?? []).some(i => i.status === 'active'),
        signoff: settingsRes.settings?.drafter_signoff_name ?? null,
      };
    },
    staleTime: 60_000,
    retry: 2,
  });

  // "Sign drafts as" is workspace-wide and overrides each person's own first
  // name, so the field holds only a saved value and is sent only when edited.
  // The user's own first name (the default) is just the placeholder.
  const signoff = signoffEdit ?? settingsQ.data?.signoff ?? '';
  const meta = user?.user_metadata;
  const myFirstName = firstNameOf(String(meta?.full_name || meta?.name || meta?.first_name || ''));

  // Detect OAuth failures from redirect URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reason = params.get('reason') || params.get('error');
    if (reason) {
      const messages: Record<string, string> = {
        bad_state: 'Connection expired — please try again.',
        access_denied: 'Access was denied. Please grant the required permissions.',
        invalid_scope: 'Some permissions were declined. Waypoint needs full access to work properly.',
        server_error: 'The provider had an error. Try again in a moment.',
      };
      setOauthError(messages[reason] || `Connection failed: ${reason}`);
      // Clean up URL
      params.delete('reason');
      params.delete('error');
      const clean = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (clean ? `?${clean}` : ''));
    }
  }, []);

  useEffect(() => {
    // The dev sandbox stays quiet unless asked (?wizard=1: the four-state capture pass, e2e/states).
    if (basePath === '/preview' && new URLSearchParams(window.location.search).get('wizard') !== '1') return;
    if (settingsQ.isLoading) return;
    if (!settingsQ.data) return;
    if (readPreference<boolean>(PREF_DISMISSED, false)) return;
    if (!settingsQ.data.hasNaics && !settingsQ.data.hasIntegration) {
      setOpen(true);
    }
  }, [settingsQ.data, settingsQ.isLoading, basePath]);

  const industryList = useQuery({
    // Public endpoint (no auth) so a hard reset can't race the session token
    // and strand the user on step 1. Retries a couple of times on transient
    // failure instead of dead-ending at "Couldn't load".
    queryKey: ['industry-list-public'],
    queryFn: () => api<{ profiles: IndustrySummary[] }>('/v1/public/industry-list'),
    enabled: open,
    staleTime: 30 * 60_000,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 4000),
  });

  // Poll integrations while on Connect or Syncing steps
  const integrationsQ = useQuery({
    queryKey: ['onboarding-integrations'],
    queryFn: listIntegrations,
    enabled: open && step >= 1,
    refetchInterval: step === 1 || step === 2 ? 3000 : false,
    staleTime: 2000,
  });

  const hubspotConnected = (integrationsQ.data ?? []).some(
    i => i.provider === 'hubspot' && i.status === 'active',
  );
  const qbConnected = (integrationsQ.data ?? []).some(
    i => i.provider === 'quickbooks' && i.status === 'active',
  );
  // ANY real integration counts as "connected" — the user can connect their
  // CRM/accounting/email from Settings (GoHighLevel, Close, Salesforce, Xero,
  // Stripe, Gmail, Fireflies…), and this wizard previously only recognized
  // HubSpot+QuickBooks, trapping anyone (e.g. a GHL user) on this step with no
  // Continue button. Companion rows (Google scope rows from Gmail, Outlook
  // Calendar from Outlook) are auto-created — exclude them so they don't
  // count as a deliberate connection on their own.
  const COMPANION_PROVIDERS = new Set(['calendar', 'gsc', 'gbp', 'youtube', 'outlook_calendar', 'teams']);
  const activeIntegrations = (integrationsQ.data ?? []).filter(
    i => i.status === 'active' && !COMPANION_PROVIDERS.has(i.provider),
  );
  const otherConnected = activeIntegrations.filter(
    i => i.provider !== 'hubspot' && i.provider !== 'quickbooks',
  );
  const hubspotSynced = (integrationsQ.data ?? []).find(
    i => i.provider === 'hubspot' && i.status === 'active',
  )?.last_synced_at;
  const qbSynced = (integrationsQ.data ?? []).find(
    i => i.provider === 'quickbooks' && i.status === 'active',
  )?.last_synced_at;
  const anyConnected = activeIntegrations.length > 0;
  const anySynced = activeIntegrations.some(i => i.last_synced_at);

  // Poll real sync status from sync_runs table
  const syncStatusQ = useQuery({
    queryKey: ['onboarding-sync-status'],
    queryFn: fetchSyncStatus,
    enabled: open && step === 2 && anyConnected,
    refetchInterval: 3000,
    staleTime: 2000,
  });

  const readinessQ = useQuery({
    queryKey: ['intelligence-readiness'],
    queryFn: () => api<{
      ready: boolean;
      historyDepthMonths: number;
      historyDepthDays: number;
      connectedIntegrations: number;
      phases: {
        dataIngestion: { status: string; contacts: number; deals: number; closedDeals: number; invoices: number; paidInvoices: number; emailThreads: number; calendarEvents: number; meetings: number };
        signalDetection: { status: string; signals: number };
        modelTraining: { status: string; trainedModels: number; liveModels: number };
        predictions: { status: string; totalPredictions: number; churnPredictions: number; expansionPredictions: number; dealClosePredictions: number; arPredictions: number };
      };
      findings: Array<{ type: string; message: string; severity: string }>;
      summary: string;
    }>('/v1/intelligence/readiness'),
    enabled: open && step === 2,
    refetchInterval: step === 2 ? 4000 : false,
    staleTime: 3000,
  });

  const syncProviders = syncStatusQ.data?.providers ?? [];
  const hubspotSync = syncProviders.find(p => p.provider === 'hubspot');
  const qbSync = syncProviders.find(p => p.provider === 'quickbooks');
  const hubspotSyncing = hubspotSync?.status === 'syncing';
  const qbSyncing = qbSync?.status === 'syncing';
  const hubspotFailed = hubspotSync?.status === 'failing';
  const qbFailed = qbSync?.status === 'failing';
  const hubspotItems = hubspotSync?.itemsSynced24h ?? 0;
  const qbItems = qbSync?.itemsSynced24h ?? 0;
  const anySyncFailed = hubspotFailed || qbFailed;

  // Auto-advance from Connect → Syncing when a provider connects
  // AND kick off the first sync immediately so data starts flowing
  useEffect(() => {
    if (step === 1 && anyConnected) {
      setStep(2);
      setConnectingProvider(null);
      api('/v1/integrations/sync-all', { method: 'POST' }).catch((err) => {
        console.error('[onboarding] Failed to trigger initial sync:', err);
      });
    }
  }, [step, anyConnected]);

  // Briefing fetched inline during sync step — no separate "Ready" step

  // Sync elapsed timer for progress feedback
  useEffect(() => {
    if (step !== 2 || anySynced) { setSyncElapsed(0); return; }
    setSyncElapsed(0);
    const interval = setInterval(() => setSyncElapsed(prev => prev + 1), 1000);
    return () => clearInterval(interval);
  }, [step, anySynced]);

  // Seed sample data for users who skip integrations
  const seedSampleData = useCallback(async () => {
    setSeedingSample(true);
    setError(null);
    try {
      await api('/v1/demo/seed-sample', { method: 'POST' });
      setSampleChosen(true);
      await queryClient.invalidateQueries();
      setStep(2);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load sample data');
    } finally {
      setSeedingSample(false);
    }
  }, [queryClient]);

  // Fetch briefing when we reach the Ready step — shows live preview
  const briefingQ = useQuery({
    queryKey: ['onboarding-briefing-preview'],
    queryFn: getMorningBriefing,
    enabled: open && step === 2,
    staleTime: 60_000,
  });

  /** Match each featured card to the best profile from the industry list */
  const featuredProfiles = useMemo(() => {
    const profiles = industryList.data?.profiles ?? [];
    return FEATURED_INDUSTRIES.map(feat => {
      const match = profiles.find(p =>
        feat.keywords.some(kw => p.naics_label.toLowerCase().includes(kw) || p.sector.toLowerCase().includes(kw)),
      );
      return match ? { ...feat, profile: match } : null;
    }).filter(Boolean) as { icon: string; keywords: string[]; profile: IndustrySummary }[];
  }, [industryList.data]);

  /** Flat filtered list for "Something else" search */
  const searchResults = useMemo(() => {
    if (!industrySearch.trim()) return [];
    const q = industrySearch.toLowerCase();
    const featuredCodes = new Set(featuredProfiles.map(f => f.profile.naics_code));
    return (industryList.data?.profiles ?? [])
      .filter(p => !featuredCodes.has(p.naics_code))
      .filter(p => p.naics_label.toLowerCase().includes(q) || p.sector.toLowerCase().includes(q))
      .slice(0, 8);
  }, [industrySearch, industryList.data, featuredProfiles]);

  /** naics null = industry skipped. An edited signoff is saved either way. */
  async function pickIndustry(naics: string | null, sector: string | null) {
    setPickedNaics(naics);
    setPickedSector(sector);
    setSavingIndustry(true);
    setError(null);
    try {
      const naicsLabel = industryList.data?.profiles.find(p => p.naics_code === naics)?.naics_label ?? '';
      const patch = {
        ...(naics ? { firm_naics_code: naics, firm_naics_label: naicsLabel } : {}),
        ...(signoffEdit !== null ? { drafter_signoff_name: signoffEdit } : {}),
      };
      // Skipped industry + untouched signoff = nothing to save (the API 400s an empty PATCH).
      if (Object.keys(patch).length > 0) {
        await api('/v1/settings', { method: 'PATCH', body: JSON.stringify(patch) });
      }
      await queryClient.invalidateQueries({ queryKey: ['industry-profile'] });
      await queryClient.invalidateQueries({ queryKey: ['onboarding-wizard-state'] });
      setStep(1);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save industry');
    } finally {
      setSavingIndustry(false);
    }
  }

  const connectHubSpot = useCallback(async () => {
    setConnectingProvider('hubspot');
    setError(null);
    try {
      // basePath is already '/dashboard' (or '/preview') — don't double-append.
      const returnTo = basePath;
      const authUrl = await getHubSpotAuthUrl(returnTo);
      window.location.href = authUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start HubSpot connection');
      setConnectingProvider(null);
    }
  }, [basePath]);

  const connectQuickBooks = useCallback(async () => {
    setConnectingProvider('quickbooks');
    setError(null);
    try {
      // basePath is already '/dashboard' (or '/preview') — don't double-append.
      const returnTo = basePath;
      const authUrl = await getQboAuthUrl(returnTo);
      window.location.href = authUrl;
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start QuickBooks connection');
      setConnectingProvider(null);
    }
  }, [basePath]);

  function finish() {
    setDismissed(true);
    setOpen(false);
    onComplete?.();
    queryClient.invalidateQueries({ queryKey: ['morning-briefing'] });
    queryClient.invalidateQueries({ queryKey: ['integrations'] });
    queryClient.invalidateQueries({ queryKey: ['onboarding'] });
    navigate(basePath);
  }

  function dismiss() {
    setSnoozed(Date.now());
    setOpen(false);
  }

  // Allow external re-open (from ContinueSetupBanner)
  function reopen() { setOpen(true); }

  // Expose reopen to parent via ref
  useEffect(() => {
    if (reopenRef) reopenRef.current = reopen;
    return () => { if (reopenRef) reopenRef.current = null; };
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-bg-0/80 backdrop-blur-xs animate-fade-in">
      <div className="relative w-full max-w-2xl rounded-2xl border border-border bg-bg-1 shadow-lift-lg overflow-hidden">
        {/* Dismiss */}
        <button
          onClick={dismiss}
          className="absolute top-3 right-3 z-raised rounded-md text-ink-dim hover:text-ink p-1"
          aria-label="Close"
        >
          <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
        </button>

        {/* Stepper */}
        <div className="px-6 pt-6 pb-3 flex items-center gap-2">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-2 flex-1">
              <div className={clsx(
                'w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-colors',
                step > i ? 'bg-success text-on-fill' : step === i ? 'bg-primary text-white' : 'bg-surface-2 text-ink-dim',
              )}>
                {step > i ? (
                  <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                ) : (
                  i + 1
                )}
              </div>
              {i < STEPS.length - 1 && <div className={clsx('h-px flex-1', step > i ? 'bg-success' : 'bg-border')} />}
            </div>
          ))}
        </div>

        <div className="px-6 pb-6">
          {/* ───── STEP 0 — Industry (Visual Cards) ───── */}
          {step === 0 && (
            <div>
              <h2 className="text-xl text-ink mb-1">Tell us about your business</h2>
              <div className="mt-3 mb-4">
                <label htmlFor="onboarding-signoff" className="mb-1.5 block text-xs font-medium text-ink-dim">
                  Sign drafts as
                </label>
                <input
                  id="onboarding-signoff"
                  type="text"
                  autoComplete="off"
                  maxLength={60}
                  value={signoff}
                  onChange={e => setSignoffEdit(e.target.value)}
                  placeholder={myFirstName ? `${myFirstName} (your first name is used by default)` : 'First name'}
                  aria-describedby="onboarding-signoff-hint"
                  className="w-full max-w-sm rounded-lg border border-border bg-surface-1 px-3 py-2 text-sm text-ink placeholder:text-ink-dim focus:outline-hidden focus:border-primary/50"
                />
                <p id="onboarding-signoff-hint" className="mt-1 text-2xs text-ink-dim">
                  Drafts are signed with each person's own first name. Set a name here only if everyone's drafts should use it.
                </p>
              </div>
              <p className="text-xs text-ink-muted mb-5">
                Pick your industry and we'll load the right benchmarks, playbooks, and KPIs instantly.
              </p>

              {industryList.isLoading ? (
                <div className="text-xs text-ink-dim py-8 text-center">Loading industries...</div>
              ) : industryList.isError || featuredProfiles.length === 0 ? (
                <div className="rounded-lg border border-border bg-surface-1 px-4 py-6 text-center">
                  <div className="text-xs text-ink-muted mb-3">
                    {industryList.isError
                      ? "Couldn't load the industry list right now."
                      : 'No industries available yet.'}
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <button onClick={() => industryList.refetch()} className="rounded-lg border border-border px-3 py-1.5 text-2xs font-medium text-ink-muted hover:bg-surface-2 hover:text-ink">Retry</button>
                    <button onClick={() => pickIndustry(null, null)} disabled={savingIndustry} className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-2xs font-medium text-primary hover:bg-primary/10">Continue without selecting →</button>
                  </div>
                </div>
              ) : !showAllIndustries ? (
                /* ── Visual card grid ── */
                <div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {featuredProfiles.map(({ icon, profile }) => (
                      <button
                        key={profile.naics_code}
                        onClick={() => pickIndustry(profile.naics_code, profile.sector)}
                        disabled={savingIndustry}
                        className={clsx(
                          'flex flex-col items-center gap-2 rounded-xl border px-3 py-4 transition-all',
                          pickedNaics === profile.naics_code
                            ? 'border-primary bg-primary/10 shadow-xs'
                            : 'border-border hover:border-primary/40 hover:bg-surface-1',
                          savingIndustry && 'opacity-50',
                        )}
                      >
                        <span className="text-2xl">{icon}</span>
                        <span className="text-xs font-medium text-ink text-center leading-tight">{profile.naics_label}</span>
                      </button>
                    ))}
                  </div>

                  {/* "Something else" search */}
                  <div className="mt-4">
                    <button
                      onClick={() => setShowAllIndustries(true)}
                      className="w-full rounded-xl border border-dashed border-border hover:border-primary/40 px-4 py-3 text-xs text-ink-muted hover:text-ink transition-colors text-center"
                    >
                      Something else? Search all industries →
                    </button>
                  </div>
                </div>
              ) : (
                /* ── Search mode ── */
                <div>
                  <div className="relative mb-3">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-dim" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                    <input
                      type="text"
                      autoFocus
                      value={industrySearch}
                      onChange={e => setIndustrySearch(e.target.value)}
                      placeholder="Search industries..."
                      className="w-full rounded-lg border border-border bg-surface-1 pl-9 pr-3 py-2.5 text-sm text-ink placeholder:text-ink-dim focus:outline-hidden focus:border-primary/50"
                    />
                  </div>

                  {industrySearch.trim() && searchResults.length > 0 ? (
                    <div className="space-y-1 max-h-[280px] overflow-y-auto">
                      {searchResults.map(p => (
                        <button
                          key={p.naics_code}
                          onClick={() => pickIndustry(p.naics_code, p.sector)}
                          disabled={savingIndustry}
                          className={clsx(
                            'w-full text-left rounded-lg border px-3 py-2.5 transition-all',
                            pickedNaics === p.naics_code
                              ? 'border-primary bg-primary/10 text-ink'
                              : 'border-border hover:border-primary/40 hover:bg-surface-1 text-ink-muted',
                            savingIndustry && 'opacity-50',
                          )}
                        >
                          <div className="text-sm font-medium text-ink">{p.naics_label}</div>
                          <div className="text-2xs text-ink-dim mt-0.5">{p.sector}</div>
                        </button>
                      ))}
                    </div>
                  ) : industrySearch.trim() ? (
                    <div className="text-xs text-ink-dim text-center py-6">
                      No matches for "{industrySearch}". Try a broader term or{' '}
                      <button onClick={() => pickIndustry(null, null)} disabled={savingIndustry} className="text-primary hover:underline">skip this step</button>.
                    </div>
                  ) : (
                    <div className="text-xs text-ink-dim text-center py-6">
                      Type to search across all industries
                    </div>
                  )}

                  <button
                    onClick={() => { setShowAllIndustries(false); setIndustrySearch(''); }}
                    className="mt-3 text-2xs text-ink-dim hover:text-ink"
                  >
                    ← Back to top picks
                  </button>
                </div>
              )}

              {error && <div className="mt-3 text-2xs text-danger">{error}</div>}

              <div className="mt-4 flex items-center justify-between text-2xs">
                <button onClick={dismiss} className="text-ink-dim hover:text-ink">Skip for now</button>
                {savingIndustry && <span className="text-ink-dim">Saving...</span>}
              </div>
            </div>
          )}

          {/* ───── STEP 1 — Connect HubSpot + QuickBooks ───── */}
          {step === 1 && (
            <div>
              <h2 className="text-xl text-ink mb-1">Connect your tools</h2>
              <p className="text-xs text-ink-muted mb-1">
                HubSpot and QuickBooks together power every prediction, risk score, and briefing Waypoint generates.
                Connect one or both — you can always add more later.
              </p>
              <p className="text-2xs text-primary/70 mb-4 font-medium">⚡ Most users connect in under 30 seconds</p>

              <div className="space-y-3">
                {/* HubSpot Card */}
                <IntegrationCard
                  name="HubSpot"
                  icon={<HubSpotIcon />}
                  connected={hubspotConnected}
                  connecting={connectingProvider === 'hubspot'}
                  description="CRM — deals, contacts, pipeline"
                  unlocks="Deal risk scoring, close predictions, pipeline velocity, follow-up queue"
                  onConnect={connectHubSpot}
                />

                {/* QuickBooks Card */}
                <IntegrationCard
                  name="QuickBooks"
                  icon={<QuickBooksIcon />}
                  connected={qbConnected}
                  connecting={connectingProvider === 'quickbooks'}
                  description="Accounting — invoices, AR, revenue"
                  unlocks="Cash runway, AR aging alerts, revenue forecasting, payment predictions"
                  onConnect={connectQuickBooks}
                />
              </div>

              {/* Already-connected acknowledgment for CRMs/sources connected
                  elsewhere (GoHighLevel, Close, Gmail, Fireflies, …) so a user
                  who's already set up isn't stranded staring at two cards. */}
              {otherConnected.length > 0 && (
                <div className="mt-3 rounded-xl border border-success/20 bg-success/4 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <svg aria-hidden="true" className="text-success shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6 9 17l-5-5" /></svg>
                    <span className="text-2xs text-ink">
                      <span className="font-semibold capitalize">
                        {otherConnected.map(i => i.provider === 'gohighlevel' ? 'GoHighLevel' : i.provider).join(', ')}
                      </span>{' '}
                      already connected — you're good to go. Continue below or add more from Settings.
                    </span>
                  </div>
                </div>
              )}

              {(error || oauthError) && (
                <div className="mt-3 rounded-lg border border-danger/20 bg-danger/5 px-3 py-2">
                  <div className="text-2xs text-danger font-medium">{error || oauthError}</div>
                  <div className="flex items-center gap-3 mt-2">
                    <button
                      onClick={() => {
                        const failedProvider = new URLSearchParams(window.location.search).get('integration');
                        setOauthError(null);
                        setError(null);
                        if (failedProvider === 'quickbooks') connectQuickBooks();
                        else connectHubSpot();
                      }}
                      className="text-2xs font-semibold text-primary hover:text-primary-bright"
                    >
                      Try again
                    </button>
                    <button
                      onClick={() => { setOauthError(null); setError(null); }}
                      className="text-2xs text-ink-dim hover:text-ink"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              )}

              {/* Try with sample data — shown when nothing is connected */}
              {!anyConnected && (
                <div className="mt-4 rounded-xl border border-primary/20 bg-primary/3 px-4 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold text-ink">Not ready to connect?</div>
                      <div className="text-2xs text-ink-muted mt-0.5">
                        Explore Waypoint with realistic sample data — deals, clients, invoices, and predictions.
                        You can connect real integrations anytime from Settings.
                      </div>
                    </div>
                    <button
                      onClick={seedSampleData}
                      disabled={seedingSample}
                      className={clsx(
                        'shrink-0 rounded-lg px-4 py-2 text-xs font-semibold transition-colors',
                        seedingSample
                          ? 'bg-surface-2 text-ink-dim cursor-wait'
                          : 'bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20',
                      )}
                    >
                      {seedingSample ? 'Loading...' : 'Try with sample data'}
                    </button>
                  </div>
                </div>
              )}

              <div className="mt-5 flex items-center justify-between">
                <button onClick={() => setStep(0)} className="text-2xs text-ink-dim hover:text-ink">← Back</button>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => {
                      navigate(`${basePath}/settings?tab=integrations`);
                      dismiss();
                    }}
                    className="text-2xs text-ink-muted hover:text-ink"
                  >
                    See all integrations
                  </button>
                  {anyConnected && (
                    <button
                      onClick={() => setStep(2)}
                      className="text-2xs text-ink-muted hover:text-ink rounded-lg border border-border px-3 py-1.5"
                    >
                      Continue →
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ───── STEP 2 — Go Live (merged sync + done) ───── */}
          {step === 2 && (
            <div>
              {sampleChosen && !anyConnected ? (
                /* ── Sample workspace: nothing is syncing, so never show "coming alive" ── */
                <>
                  <h2 className="text-xl text-ink mb-1">Your sample workspace is ready</h2>
                  <p className="text-xs text-ink-muted mb-5">
                    Look around with made-up deals, clients and meetings. Connect your real tools any time
                    and the sample data is removed automatically.
                  </p>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={finish}
                      className="rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-white hover:brightness-110"
                    >
                      Explore the sample workspace →
                    </button>
                    <button
                      onClick={() => setStep(1)}
                      className="rounded-lg border border-border px-4 py-2 text-xs text-ink-muted hover:text-ink"
                    >
                      Connect your real tools
                    </button>
                  </div>
                </>
              ) : !anySynced ? (
                /* ── Phase A: Data is syncing — real status from sync_runs ── */
                <>
                  <h2 className="text-xl text-ink mb-1">Your data is coming alive...</h2>
                  <p className="text-xs text-ink-muted mb-5">
                    Waypoint is importing your history and running the first analysis.
                  </p>

                  {/* Real sync progress from intelligence readiness */}
                  <div className="rounded-xl bg-surface-1 border border-border px-4 py-4 mb-4 space-y-3">
                    {(() => {
                      const r = readinessQ.data;
                      const di = r?.phases?.dataIngestion;
                      const ml = r?.phases?.modelTraining;
                      const pred = r?.phases?.predictions;
                      return (
                        <>
                          <SyncAnimationLine text="Connecting to your accounts" done={anyConnected} />
                          <SyncAnimationLine
                            text={di && (di.contacts + di.deals) > 0
                              ? `Imported ${di.contacts} contacts and ${di.deals} deals${r?.historyDepthMonths ? ` (${r.historyDepthMonths} months of history)` : ''}`
                              : 'Importing contacts, deals, and history'}
                            done={di ? di.contacts > 0 || di.deals > 0 : false}
                            active={(hubspotSyncing || qbSyncing) && !(di && (di.contacts + di.deals) > 0)}
                          />
                          <SyncAnimationLine
                            text={di && di.invoices > 0
                              ? `Analyzed ${di.invoices} invoices (${di.paidInvoices} payment outcomes learned)`
                              : 'Analyzing invoices and payments'}
                            done={di ? di.invoices > 0 : false}
                            active={qbSyncing && !(di && di.invoices > 0)}
                          />
                          <SyncAnimationLine
                            text={di && (di.emailThreads + di.meetings) > 0
                              ? `Processed ${di.emailThreads} email threads and ${di.meetings} meetings`
                              : 'Scanning email and meeting history'}
                            done={di ? (di.emailThreads + di.meetings) > 0 : false}
                            active={anySynced && !(di && (di.emailThreads + di.meetings) > 0)}
                          />
                          <SyncAnimationLine
                            text={ml && ml.liveModels > 0
                              ? `${ml.liveModels} ${ml.liveModels === 1 ? 'model' : 'models'} live on your data`
                              : NO_LIVE_MODEL_COPY}
                            done={ml ? ml.liveModels > 0 : false}
                            active={false}
                          />
                          <SyncAnimationLine
                            text={pred && pred.totalPredictions > 0
                              ? `${pred.totalPredictions} predictions generated`
                              : 'Generating predictions'}
                            done={pred ? pred.totalPredictions > 0 : false}
                            active={ml ? ml.liveModels > 0 && !(pred && pred.totalPredictions > 0) : false}
                          />
                        </>
                      );
                    })()}
                    {readinessQ.data && (readinessQ.data.phases.dataIngestion.contacts + readinessQ.data.phases.dataIngestion.deals + readinessQ.data.phases.dataIngestion.invoices) > 0 && (
                      <div className="text-2xs text-ink-dim text-center pt-1">
                        {readinessQ.data.summary}
                      </div>
                    )}
                  </div>

                  {/* Provider status with real sync state */}
                  <div className="space-y-2 mb-4">
                    {hubspotConnected && (
                      <SyncStatusRow
                        name="HubSpot"
                        icon={<HubSpotIcon />}
                        synced={!!hubspotSynced}
                        elapsed={!hubspotSynced ? syncElapsed : undefined}
                        failed={hubspotFailed}
                        items={hubspotItems}
                      />
                    )}
                    {qbConnected && (
                      <SyncStatusRow
                        name="QuickBooks"
                        icon={<QuickBooksIcon />}
                        synced={!!qbSynced}
                        elapsed={!qbSynced ? syncElapsed : undefined}
                        failed={qbFailed}
                        items={qbItems}
                      />
                    )}
                    {!hubspotConnected && !qbConnected && (
                      <div className="text-xs text-ink-muted text-center py-2">
                        Using sample data — your briefing is building now.
                      </div>
                    )}
                  </div>

                  {anySyncFailed && (
                    <div className="rounded-lg bg-danger/10 border border-danger/20 px-3 py-2 mb-3 text-xs text-danger flex items-center justify-between">
                      <span>Sync encountered an error.</span>
                      <button
                        onClick={async () => {
                          try {
                            await api('/v1/integrations/sync-all', { method: 'POST' });
                            syncStatusQ.refetch();
                          } catch { /* silent */ }
                        }}
                        className="text-2xs font-semibold rounded-sm px-2 py-0.5 bg-danger/10 hover:bg-danger/20"
                      >
                        Retry sync
                      </button>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <button onClick={() => setStep(1)} className="text-2xs text-ink-dim hover:text-ink">← Back</button>
                    <div className="flex items-center gap-2">
                      {syncElapsed >= 5 && (
                        <span className="text-2xs text-ink-dim">{syncElapsed}s</span>
                      )}
                      {syncElapsed >= 15 && (
                        <button
                          onClick={finish}
                          className="text-2xs rounded-lg px-3 py-1.5 text-ink-muted hover:text-ink border border-border"
                        >
                          Go to dashboard — sync continues in background →
                        </button>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                /* ── Phase B: Sync complete — seamless transition to live insights ── */
                <>
                  <div className="rounded-xl bg-success/10 border border-success/20 px-4 py-4 mb-4 animate-fade-in">
                    {/* "live" only with a servable model (readiness phases.modelTraining.liveModels); a synced workspace may have none. */}
                    <div className="text-lg font-semibold text-success text-center">
                      {(readinessQ.data?.phases.modelTraining.liveModels ?? 0) > 0 ? 'Your intelligence is live.' : 'Your data is synced.'}
                    </div>
                    {(readinessQ.data?.phases.modelTraining.liveModels ?? 0) === 0 ? (
                      <div className="text-xs text-ink-muted text-center mt-1">{NO_LIVE_MODEL_COPY}</div>
                    ) : readinessQ.data?.historyDepthMonths ? (
                      <div className="text-xs text-ink-muted text-center mt-1">
                        Analyzed {readinessQ.data.historyDepthMonths} months of your business history
                      </div>
                    ) : (
                      <div className="text-xs text-ink-muted text-center mt-1">
                        Tuned for {pickedSector ?? 'your business'} — your intelligence mesh is active.
                      </div>
                    )}
                  </div>

                  {/* Connected integrations */}
                  {anyConnected && (
                    <div className="rounded-xl bg-surface-1 border border-border px-4 py-3 mb-4">
                      <div className="flex items-center gap-4">
                        {hubspotConnected && (
                          <span className="flex items-center gap-1.5 text-xs text-ink">
                            <HubSpotIcon /><span>HubSpot</span>
                            <svg aria-hidden="true" className="text-success" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                          </span>
                        )}
                        {qbConnected && (
                          <span className="flex items-center gap-1.5 text-xs text-ink">
                            <QuickBooksIcon /><span>QuickBooks</span>
                            <svg aria-hidden="true" className="text-success" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Intelligence findings summary */}
                  {readinessQ.data && readinessQ.data.findings.length > 0 && (
                    <div className="rounded-xl bg-surface-1 border border-border px-4 py-3 mb-4 space-y-2 animate-fade-in">
                      <div className="text-2xs text-ink-muted font-semibold">What we found in your data:</div>
                      <ul className="space-y-1.5">
                        {readinessQ.data.findings.slice(0, 5).map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-[11.5px] text-ink">
                            <span className={clsx(
                              'mt-0.5 shrink-0 h-1.5 w-1.5 rounded-full',
                              f.severity === 'high' ? 'bg-danger' : f.severity === 'medium' ? 'bg-warn' : 'bg-success',
                            )} />
                            <span>{f.message}</span>
                          </li>
                        ))}
                      </ul>
                      {readinessQ.data.phases.modelTraining.liveModels > 0 && (
                        <div className="text-2xs text-primary/70 font-medium pt-1">
                          {readinessQ.data.phases.modelTraining.liveModels} {readinessQ.data.phases.modelTraining.liveModels === 1 ? 'model' : 'models'} live. Others go live once they beat their baselines.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Live briefing preview */}
                  {briefingQ.data && briefingQ.data.items.length > 0 ? (
                    <div className="rounded-xl bg-surface-1 border border-border px-4 py-3 mb-4 space-y-2 animate-fade-in">
                      <div className="text-2xs text-ink-muted font-semibold">Your first briefing:</div>
                      <ul className="space-y-1.5">
                        {briefingQ.data.items.slice(0, 3).map((item, i) => (
                          <li key={item.id || i} className="flex items-start gap-2 text-[11.5px] text-ink">
                            <span className={clsx(
                              'mt-0.5 shrink-0 h-1.5 w-1.5 rounded-full',
                              item.priority === 'high' ? 'bg-danger' : item.priority === 'medium' ? 'bg-warn' : 'bg-success',
                            )} />
                            <span>{item.message}</span>
                          </li>
                        ))}
                      </ul>
                      {briefingQ.data.items.length > 3 && (
                        <div className="text-2xs text-ink-dim pt-1">
                          +{briefingQ.data.items.length - 3} more on your Command Center
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rounded-xl bg-surface-1 px-4 py-3 mb-4 space-y-2">
                      <div className="text-2xs text-ink-muted">Try asking Waypoint:</div>
                      <ul className="space-y-1 text-[11.5px] text-ink">
                        <li>· What should I focus on today?</li>
                        <li>· Which deals are at risk this week?</li>
                        <li>· Who owes me the most money?</li>
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center justify-end">
                    <button
                      onClick={finish}
                      className="rounded-lg bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:opacity-90 animate-fade-in"
                    >
                      Go to my dashboard →
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Sub-components ─── */

function IntegrationCard({
  name, icon, connected, connecting, description, unlocks, onConnect,
}: {
  name: string;
  icon: React.ReactNode;
  connected: boolean;
  connecting: boolean;
  description: string;
  unlocks: string;
  onConnect: () => void;
}) {
  return (
    <div className={clsx(
      'rounded-xl border px-4 py-3 transition-all',
      connected ? 'border-success/30 bg-success/4' : 'border-border hover:border-primary/40',
    )}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="shrink-0 mt-0.5">{icon}</div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-ink">{name}</span>
              {connected && (
                <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-2xs font-semibold text-success">
                  <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                  Connected
                </span>
              )}
            </div>
            <div className="text-2xs text-ink-muted mt-0.5">{description}</div>
            <div className="text-2xs text-ink-dim mt-1">
              Unlocks: <span className="text-primary">{unlocks}</span>
            </div>
          </div>
        </div>
        {!connected && (
          <button
            onClick={onConnect}
            disabled={connecting}
            className={clsx(
              'shrink-0 rounded-lg px-4 py-2 text-xs font-semibold transition-colors',
              connecting
                ? 'bg-surface-2 text-ink-dim cursor-wait'
                : 'bg-primary text-white hover:opacity-90',
            )}
          >
            {connecting ? 'Connecting...' : 'Connect'}
          </button>
        )}
      </div>
    </div>
  );
}

function SyncStatusRow({ name, icon, synced, elapsed, failed, items }: {
  name: string; icon: React.ReactNode; synced: boolean; elapsed?: number; failed?: boolean; items?: number;
}) {
  return (
    <div className={clsx(
      'flex items-center gap-3 rounded-lg border px-4 py-3',
      failed ? 'border-danger/30 bg-danger/5' : 'border-border bg-surface-1',
    )}>
      <div className="shrink-0">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-ink">{name}</div>
        {!synced && !failed && items !== undefined && items > 0 && (
          <div className="text-2xs text-ink-dim mt-0.5">
            {items} records imported...
          </div>
        )}
        {!synced && !failed && elapsed !== undefined && elapsed >= 60 && (!items || items === 0) && (
          <div className="text-2xs text-ink-dim mt-0.5">
            Large dataset — syncing in background
          </div>
        )}
        {failed && (
          <div className="text-2xs text-danger mt-0.5">
            Sync failed — try reconnecting in Settings
          </div>
        )}
      </div>
      {synced ? (
        <span className="flex items-center gap-1.5 text-2xs font-semibold text-success">
          <svg aria-hidden="true" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
          Synced
        </span>
      ) : failed ? (
        <span className="flex items-center gap-1.5 text-2xs text-danger font-semibold">
          ! Failed
        </span>
      ) : (
        <span className="flex items-center gap-1.5 text-2xs text-ink-muted">
          <span className="inline-block h-3 w-3 rounded-full border-2 border-primary/40 border-t-primary animate-spin" />
          Syncing...
        </span>
      )}
    </div>
  );
}

/** Animated progress line — checkmark when done, spinner when active, dim when pending */
function SyncAnimationLine({ text, done, active }: { text: string; done: boolean; active?: boolean }) {
  return (
    <div className={clsx(
      'flex items-center gap-2.5 text-xs transition-all duration-500',
      done ? 'text-ink' : active ? 'text-ink' : 'text-ink-dim/40',
    )}>
      {done ? (
        <svg aria-hidden="true" className="text-success shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
      ) : active ? (
        <span className="shrink-0 inline-block h-3.5 w-3.5 rounded-full border-2 border-primary/40 border-t-primary animate-spin" />
      ) : (
        <span className="shrink-0 h-3.5 w-3.5 rounded-full border border-border" />
      )}
      <span>{text}{active ? '...' : ''}</span>
    </div>
  );
}

/* ─── Provider Icons ─── */

function HubSpotIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M17.5 8.2V5.7a1.7 1.7 0 0 0 1-1.5v-.1a1.7 1.7 0 0 0-1.7-1.7h-.1a1.7 1.7 0 0 0-1.7 1.7v.1c0 .7.4 1.2 1 1.5v2.5a5.2 5.2 0 0 0-2.7 1.3l-7.2-5.6a2 2 0 0 0 .1-.6 2.1 2.1 0 1 0-2.1 2.1c.4 0 .8-.1 1.1-.3l7.1 5.5a5.2 5.2 0 0 0 .1 5.3l-2.1 2.1a1.8 1.8 0 0 0-.5-.1 1.9 1.9 0 1 0 1.9 1.9 1.8 1.8 0 0 0-.1-.5l2.1-2.1a5.2 5.2 0 1 0 3.7-8.8z" fill="#FF7A59"/>
    </svg>
  );
}

function QuickBooksIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="#2CA01C"/>
      <path d="M7 9.5a2.5 2.5 0 0 0 0 5h2v-1h-2a1.5 1.5 0 0 1 0-3h1v5.5h1v-6.5H7zm10 5a2.5 2.5 0 0 0 0-5h-2v1h2a1.5 1.5 0 0 1 0 3h-1v-5.5h-1v6.5h2z" fill="white"/>
    </svg>
  );
}
